import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiUser, FiUsers, FiShield, FiTrendingUp, FiPieChart, FiMenu, FiX, FiLayers, FiLogOut, FiUserPlus, FiLock, FiBriefcase, FiDatabase, FiPackage } from 'react-icons/fi';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { getStoredSession } from '../../api/authStorage';
import PingLogo from './PingLogo';
import IssuerLogin from '../TokenIssuerManager/IssuerLogin';

const normalizeAuthRole = (role) => String(role || '').replace(/\s+/g, '').toLowerCase();

const sessionHasAuthorizedTokenizerRole = (session) => {
  if (!session) return false;
  const roles =
    Array.isArray(session.roles) && session.roles.length > 0
      ? session.roles
      : session.role
        ? [session.role]
        : [];
  return roles.some((r) => normalizeAuthRole(r) === 'authorizedtokenizer');
};

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: rgba(15, 15, 35, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(79, 172, 254, 0.2);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
`;

const LogoSection = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #ffffff;
  margin-right: 3rem;
  gap: 0.75rem;
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const MainTitle = styled.h1`
  font-size: 1.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  line-height: 1.2;
`;

const SubTitle = styled.span`
  font-size: 0.75rem;
  color: #a0a0a0;
  font-weight: 400;
  line-height: 1;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex: 1;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    background: rgba(15, 15, 35, 0.98);
    flex-direction: column;
    padding: 2rem;
    border-bottom: 1px solid #2d2d5f;
    max-height: calc(100vh - 80px);
    overflow-y: auto;
    
    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(79, 172, 254, 0.3);
      border-radius: 3px;
      
      &:hover {
        background: rgba(79, 172, 254, 0.5);
      }
    }
  }
`;

const TokenizerButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  color: #fff;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.3);
  margin-right: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(79, 172, 254, 0.4);
  }

  svg {
    font-size: 1.1rem;
  }
`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  @media (min-width: 769px) {
    flex-direction: row;
    gap: 1rem;
  }
`;

const SectionTitle = styled.h3`
  color: #4facfe;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 1rem 0 0.5rem 0;
  border-bottom: 1px solid rgba(79, 172, 254, 0.2);
  padding-bottom: 0.5rem;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: ${props => props.active ? '#4facfe' : '#a0a0a0'};
  font-weight: ${props => props.active ? '600' : '400'};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    color: #4facfe;
    background: rgba(79, 172, 254, 0.1);
  }

  svg {
    font-size: 1.1rem;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ConnectButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const IssuerButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  border: 1px solid rgba(79, 172, 254, 0.3);
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;

  &:hover {
    background: rgba(79, 172, 254, 0.1);
    border-color: #4facfe;
  }

  &.logged-in {
    background: rgba(34, 197, 94, 0.2);
    border-color: rgba(34, 197, 94, 0.3);
    color: #22c55e;

    &:hover {
      background: rgba(34, 197, 94, 0.3);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  color: #ffffff;
  font-size: 1.5rem;
  margin-left: auto;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showIssuerLogin, setShowIssuerLogin] = useState(false);
  const { currentIssuer, clearCurrentIssuer, isAdmin, isAuthorizedTokenizer, isLegalReviewer } =
    useTokenIssuer();

  const showLegalReviewsNav = isAdmin || isLegalReviewer || !isAuthorizedTokenizer;
  const showAuthorizedTokenizersNav = isAdmin || isAuthorizedTokenizer || !isLegalReviewer;

  const userFacingItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FiTrendingUp },
    ...(currentIssuer ? [{ path: '/account', label: 'My account', icon: FiLock }] : []),
    ...(isAdmin
      ? [
          { path: '/admin/users', label: 'User management', icon: FiUserPlus },
          { path: '/admin/legal-review-firms', label: 'Legal reviewer firms', icon: FiBriefcase }
        ]
      : []),
    ...(isAuthorizedTokenizer
      ? [
          { path: '/my-assets', label: 'Assets Registry', icon: FiDatabase },
          { path: '/bulk-assets-registry', label: 'Bulk assets registry', icon: FiPackage }
        ]
      : []),
    ...(isLegalReviewer
      ? [{ path: '/legal-firm/assets', label: 'Firm legal assets', icon: FiBriefcase }]
      : []),
    ...(showLegalReviewsNav
      ? [{ path: '/legal-reviews', label: 'Legal reviews', icon: FiShield }]
      : []),
    ...(showAuthorizedTokenizersNav
      ? [{ path: '/authorized-tokenizers', label: 'Authorized tokenizers', icon: FiUsers }]
      : []),
    { path: '/portfolio', label: 'Portfolio', icon: FiPieChart },
  ];

  const handleIssuerLogin = () => {
    const session = getStoredSession();
    if (sessionHasAuthorizedTokenizerRole(session)) {
      window.location.href = '/tokenization';
    } else {
      window.location.href = '/dashboard';
    }
  };

  const handleIssuerLogout = () => {
    clearCurrentIssuer();
  };

  return (
    <>
    <HeaderContainer>
      <LogoSection to="/">
        <PingLogo size="32px" />
        <BrandText>
          <MainTitle>Core Tokenizer</MainTitle>
          <SubTitle>RWA Tokenization Platform</SubTitle>
        </BrandText>
      </LogoSection>

      <TokenizerButton to="/tokenization">
        <FiLayers />
        Tokenize Assets
      </TokenizerButton>

      <Nav isOpen={isMenuOpen}>
        <NavSection>
          <SectionTitle>Platform</SectionTitle>
          {userFacingItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                active={location.pathname === item.path ? 1 : 0}
                onClick={() => setIsMenuOpen(false)}
              >
                <Icon />
                {item.label}
              </NavLink>
            );
          })}
        </NavSection>
      </Nav>

      <UserSection>
        {currentIssuer ? (
          <IssuerButton className="logged-in" onClick={handleIssuerLogout}>
            <FiShield />
            {currentIssuer.name}
            <FiLogOut />
          </IssuerButton>
        ) : (
          <ConnectButton type="button" onClick={() => setShowIssuerLogin(true)}>
            <FiUser style={{ marginRight: '0.5rem' }} />
            Login
          </ConnectButton>
        )}
      </UserSection>

      <MobileMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
        {isMenuOpen ? <FiX /> : <FiMenu />}
      </MobileMenuButton>
    </HeaderContainer>
    <IssuerLogin
      isOpen={showIssuerLogin}
      onClose={() => setShowIssuerLogin(false)}
      onLogin={handleIssuerLogin}
    />
    </>
  );
};

export default Header; 